<template>
    <div class="receipt-card-container mb-3">
        <!-- Card Header -->
        <div class="d-flex justify-content-between align-items-center mb-4">
            <h3 class="card-title-receipt mb-0">QR mendatang dari Dealer</h3>
            <div class="date-picker-wrapper">
                <span class="calendar-icon">
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.6667 2.66667H3.33333C2.59695 2.66667 2 3.26362 2 4V13.3333C2 14.0697 2.59695 14.6667 3.33333 14.6667H12.6667C13.403 14.6667 14 14.0697 14 13.3333V4C14 3.26362 13.403 2.66667 12.6667 2.66667Z" stroke="#2F2F2F" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M10.6667 1.33333V4" stroke="#2F2F2F" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M5.33333 1.33333V4" stroke="#2F2F2F" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2 6.66667H14" stroke="#2F2F2F" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M4.66667 9.33333H5.33333" stroke="#2F2F2F" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M8 9.33333H8.66667" stroke="#2F2F2F" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M10.6667 9.33333H11.3333" stroke="#2F2F2F" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M4.66667 12H5.33333" stroke="#2F2F2F" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M8 12H8.66667" stroke="#2F2F2F" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M10.6667 12H11.3333" stroke="#2F2F2F" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </span>
                <flat-pickr v-model="selected.date" :config="dateRangeConfig" class="flatpickr-custom-input"></flat-pickr>
            </div>
        </div>

        <!-- Table -->
        <div class="w-100 overflow-auto">
            <table class="table w-100 custom-receipt-table">
                <thead>
                    <tr>
                        <th>Kode BAST</th>
                        <th>Jumlah</th>
                        <th>Tanggal Pengiriman</th>
                        <th>Status</th>
                        <th>Tanggal Penerimaan</th>
                    </tr>
                </thead>
                <tbody v-if="data && data.length">
                    <tr v-for="(dat, index) in data" :key="dat.id || index">
                        <td class="cell-bast-no">{{ dat.bast_no ? dat.bast_no : '-' }}</td>
                        <td class="cell-qty">{{ dat.qty ? numberWithCommas(dat.qty) : '-' }}</td>
                        <td class="cell-date">{{ formatDeliveryDate(dat.delivery_at) }}</td>

                        <!-- Status Column -->
                        <td class="cell-status">
                            <span v-if="dat.status === 'RECEIVED'">Received</span>
                            <div v-else class="status-select-wrapper">
                                <select
                                    v-model="dat.status"
                                    class="status-dropdown-select"
                                    @change="confirmUpdate(dat)"
                                >
                                    <option value="INTRANSIT" disabled>In-Transit</option>
                                    <option value="RECEIVED">Received</option>
                                </select>
                                <i class="fas fa-caret-down status-caret-icon"></i>
                            </div>
                        </td>

                        <!-- Tanggal Penerimaan -->
                        <td class="cell-received">
                            <span>{{ formatReceivedDate(dat.received_at) }}</span>
                            <i v-if="dat.received_at" class="fa fa-file-download ml-2 clickAble" title="Download BAST" @click="printReceiptMethod(dat)"></i>
                        </td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr>
                        <td colspan="5" class="text-center py-4 text-muted">Tidak ada data</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-end align-items-center pt-3 pb-1" v-if="data && config.total > 0">
            <span class="pagination-info mr-3">
                Showing {{ (config.page - 1) * config.limit + 1 }}-{{ Math.min(config.page * config.limit, config.total) }} of {{ config.total }} Items
            </span>
            <div class="pagination-nav d-flex align-items-center">
                <button
                    type="button"
                    class="btn-nav-page"
                    :disabled="config.page <= 1"
                    @click="goToPage(1)"
                    aria-label="First Page"
                >
                    <i class="fa fa-angle-double-left"></i>
                </button>
                <button
                    type="button"
                    class="btn-nav-page"
                    :disabled="config.page <= 1"
                    @click="goToPage(config.page - 1)"
                    aria-label="Previous Page"
                >
                    <i class="fa fa-angle-left"></i>
                </button>
                <span class="active-page-number">{{ config.page }}</span>
                <button
                    type="button"
                    class="btn-nav-page"
                    :disabled="config.page >= config.lastPage"
                    @click="goToPage(config.page + 1)"
                    aria-label="Next Page"
                >
                    <i class="fa fa-angle-right"></i>
                </button>
                <button
                    type="button"
                    class="btn-nav-page"
                    :disabled="config.page >= config.lastPage"
                    @click="goToPage(config.lastPage)"
                    aria-label="Last Page"
                >
                    <i class="fa fa-angle-double-right"></i>
                </button>
            </div>
        </div>

        <!-- Modal Start -->
        <!-- Confirmation Modal Start -->
        <transition name="modal">
            <div v-if="modal.confirmation" class="confirm-modal-mask">
                <div class="confirm-modal-wrapper" @click="modal.confirmation = false">
                    <div class="confirm-modal-container" @click.stop>
                        <!-- Header -->
                        <div class="confirm-header d-flex justify-content-between align-items-center">
                            <h6 class="confirm-title mb-0">Konfirmasi Penerimaan QR</h6>
                            <button type="button" class="btn btn-close-confirm" @click="modal.confirmation = false">
                                <i class="fas fa-times"></i>
                            </button>
                        </div>

                        <!-- Body Content -->
                        <div class="confirm-body text-center">
                            <div class="confirm-icon-wrapper">
                                <svg width="108" height="98" viewBox="0 0 124 112" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M53.493 2.18918C61.9236 -2.48607 72.6478 0.58571 77.359 8.94096L122.425 87.1547C123.417 89.4893 123.851 91.3877 123.975 93.3597C124.223 97.9674 122.611 102.446 119.45 105.893C116.288 109.327 112.011 111.354 107.362 111.6H16.6092C14.6875 111.483 12.7659 111.047 10.9682 110.371C1.97967 106.747 -2.3596 96.5482 1.29778 87.7077L46.6741 8.88567C48.2239 6.11492 50.5795 3.72507 53.493 2.18918ZM61.9856 76.0902C59.0101 76.0902 56.5305 78.5476 56.5305 81.5027C56.5305 84.4454 59.0101 86.909 61.9856 86.909C64.9611 86.909 67.3786 84.4454 67.3786 81.4351C67.3786 78.4923 64.9611 76.0902 61.9856 76.0902ZM61.9856 37.7604C59.0101 37.7604 56.5305 40.1503 56.5305 43.1054V60.4855C56.5305 63.4344 59.0101 65.898 61.9856 65.898C64.9611 65.898 67.3786 63.4344 67.3786 60.4855V43.1054C67.3786 40.1503 64.9611 37.7604 61.9856 37.7604Z" fill="#F36F21"/>
                                </svg>
                            </div>

                            <div class="confirm-text">
                                <p class="mb-1">Anda akan menerima <strong>{{ modal.confirmationData ? numberWithCommas(modal.confirmationData.qty) : 0 }}</strong> QR</p>
                                <p class="mb-0 text-muted-sub">Apa anda yakin jumlahnya sudah benar?</p>
                            </div>
                        </div>

                        <!-- Actions -->
                        <div class="confirm-actions d-flex justify-content-center align-items-center">
                            <button 
                                type="button" 
                                class="btn btn-confirm-batal" 
                                @click="modal.confirmation = false"
                            >
                                Batal
                            </button>
                            <button 
                                type="button" 
                                class="btn btn-confirm-submit" 
                                @click="modal.bastConfirmation = true; modal.confirmation = false;"
                            >
                                Ya, Proses
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
        <!-- Confirmation Modal End -->

        <!-- BAST Confirmation Modal Start -->
        <bast-confirmation
            v-if="modal.bastConfirmation"
            :data="modal.confirmationData"
            @closeModal="modal.bastConfirmation = false"
            @goBack="modal.bastConfirmation = false; modal.confirmation = true;"
            @refreshData="handleRefreshData"
        ></bast-confirmation>
        <!-- BAST Confirmation Modal End -->

        <print-receipt ref="printReceipt" :data="receiptData"/>
    </div>
</template>

<script>
    import moment from 'moment';
    import 'moment/locale/id';

    import bastConfirmation from './bastConfirmation.vue';
    import printReceipt from './printReceipt.vue';

    const RANGE_SEPARATOR = ' s/d ';
    const DEFAULT_RANGE_DAYS = 14;

    export default {
        data () {
            let start = moment().subtract(DEFAULT_RANGE_DAYS, 'days').format('YYYY-MM-DD');
            let end = moment().format('YYYY-MM-DD');
            return {
                modal: {
                    confirmation: false,
                    confirmationData: null,
                    bastConfirmation: false,
                },
                moment: moment,
                dateRangeConfig: {
                    mode: "range",
                    altFormat: "d-m-Y",
                    dateFormat: "Y-m-d",
                    altInput: true,
                    altInputClass: "flatpickr-custom-input",
                    locale: {
                        rangeSeparator: RANGE_SEPARATOR
                    },
                    onClose: this.handleDateRangeClose
                },
                data : null,
                options : {
                    status: [
                        {label: 'In-Transit', value: 'INTRANSIT', isDisabled: true},
                        {label: 'Received', value: 'RECEIVED'},
                    ]
                },
                selected: {
                    date: `${start}${RANGE_SEPARATOR}${end}`,
                },
                filter : {
                    start_date: start,
                    end_date: end,
                    status: null,
                },
                config : {
                    page: 1,
                    limit: 5,
                    lastPage: 0,
                    total: 0,
                },
                receiptData: null,
            }
        },
        components: {
            bastConfirmation,
            printReceipt,
        },
        mounted () {
            this.fetchPaginated();
        },
        watch : {
            'filter' : {
                deep: true,
                handler () {
                    this.config.page = 1;
                    this.fetchPaginated();
                },
            },
            'config.page'() {
                this.fetchPaginated();
            }
        },
        methods : {
            numberWithCommas(x) {
                if (!x && x !== 0) return '0';
                return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            },
            formatDeliveryDate(val) {
                if (!val) return '-';
                return moment(val).locale('id').format('DD MMM YYYY | HH:mm');
            },
            formatReceivedDate(val) {
                if (!val) return '-';
                return moment(val).locale('id').format('DD MMM YYYY | HH:mm');
            },
            goToPage(page) {
                if (page >= 1 && page <= this.config.lastPage) {
                    this.config.page = page;
                }
            },
            /**
             * Applies the picked range from flatpickr's Date objects, so the
             * filter never depends on how the input text is formatted.
             */
            handleDateRangeClose(selectedDates) {
                if (!selectedDates.length) return;
                const [first, last = first] = selectedDates;
                this.filter.start_date = moment(first).format('YYYY-MM-DD');
                this.filter.end_date = moment(last).format('YYYY-MM-DD');
            },
            confirmUpdate(data) {
                this.$nextTick(() => {
                    data.status = 'INTRANSIT';
                });
                this.modal.confirmationData = data;
                this.modal.confirmation = true;
            },
            handleRefreshData() {
                this.config.page = 1;
                this.fetchPaginated();
            },
            async printReceiptMethod(data) {
                try {
                    let id = data.id;
                    let response = await this.$http.get('/api/qrs/issue/' + id);

                    this.receiptData = response.data.data;
                    this.$refs.printReceipt.generateReport();
                }
                catch(err) {
                    console.log(err);
                }
            },
            fetchPaginated () {
                let filter = JSON.parse(JSON.stringify(this.filter));

                this.$http.get(`/api/qrs/issue`, {params: {...filter, ...{page: this.config.page, limit: this.config.limit}}})
                .then(res => {
                    let data = res.data && res.data.data;
                    if (data && Array.isArray(data)) {
                        this.data = data;
                        this.config.lastPage = res.data.meta ? res.data.meta.lastPage : Math.ceil(data.length / this.config.limit);
                        this.config.total = res.data.meta ? res.data.meta.total : data.length;
                    } else {
                        this.data = [];
                        this.config.lastPage = 1;
                        this.config.total = 0;
                    }
                })
                .catch((err) => {
                    console.log(err);
                    this.data = [];
                    this.config.lastPage = 1;
                    this.config.total = 0;
                });
            },
        }
    }
</script>

<style>
    .date-picker-wrapper .flatpickr-custom-input {
        font-size: 12px !important;
        min-width: 213px !important;
        height: 32px !important;
        background-color: #FFFFFF !important;
        padding: 4px 12px 4px 34px !important;
        cursor: pointer !important;
        border: 1px solid #F3F3F3 !important;
        border-radius: 4px !important;
        color: #2F2F2F !important;
        outline: none !important;
        box-shadow: none !important;
    }
    .date-picker-wrapper .flatpickr-custom-input:focus {
        border-color: #F36F21 !important;
    }
</style>

<style scoped>
    .clickAble:hover {
        cursor: pointer;
        opacity: 70%;
    }

    .receipt-card-container {
        background: #FFFFFF;
        border-radius: 8px;
        padding: 24px 24px 16px 24px;
    }

    .card-title-receipt {
        font-size: 16px;
        font-weight: 600;
        color: #2F2F2F;
    }

    .date-picker-wrapper {
        position: relative;
        display: inline-flex;
        align-items: center;
    }

    .calendar-icon {
        position: absolute;
        left: 11px;
        z-index: 2;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
    }

    .custom-receipt-table {
        margin-bottom: 0;
        border-collapse: collapse;
    }

    .custom-receipt-table thead th {
        font-size: 12px;
        font-weight: 600;
        color: #2F2F2F;
        border-top: none;
        border-bottom: 1px solid #F0F0F0;
        padding: 12px 16px;
        vertical-align: middle;
        white-space: nowrap;
    }

    .custom-receipt-table tbody tr {
        border-bottom: 1px solid #F6F7F8;
    }

    .custom-receipt-table tbody tr:last-child {
        border-bottom: 1px solid #F0F0F0;
    }

    .custom-receipt-table tbody td {
        font-size: 12px;
        font-weight: 400;
        color: #2F2F2F;
        padding: 14px 16px;
        vertical-align: middle;
        border-top: none;
    }

    .cell-bast-no {
        font-weight: 400;
    }

    .cell-qty {
        font-weight: 400;
    }

    .cell-date {
        font-weight: 400;
    }

    .cell-received {
        font-weight: 400;
    }

    .status-select-wrapper {
        position: relative;
        display: inline-block;
        width: 151px;
    }

    .status-dropdown-select {
        width: 100%;
        height: 30px;
        border: 1px solid #F3F3F3;
        border-radius: 4px;
        background-color: #FFFFFF;
        font-size: 12px;
        color: #2F2F2F;
        padding: 4px 26px 4px 12px;
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        cursor: pointer;
        outline: none;
        transition: border-color 0.2s;
    }

    .status-dropdown-select:hover,
    .status-dropdown-select:focus {
        border-color: #D1D5DB;
    }

    .status-dropdown-select option:disabled {
        color: #A5A5A5;
    }

    .status-caret-icon {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 11px;
        color: #374151;
        pointer-events: none;
    }

    .pagination-info {
        font-size: 12px;
        color: #CBCBCB;
        font-weight: 400;
    }

    .pagination-nav {
        display: inline-flex;
        align-items: center;
    }

    .btn-nav-page {
        background: transparent;
        border: none;
        color: #9CA3AF;
        font-size: 12px;
        padding: 3px 6px;
        margin: 0 1px;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: color 0.15s;
        line-height: 1;
    }

    .btn-nav-page:hover:not(:disabled) {
        color: #374151;
    }

    .btn-nav-page:disabled {
        color: #D1D5DB;
        cursor: not-allowed;
    }

    .active-page-number {
        font-size: 12px;
        font-weight: 400;
        color: #4B4B4B;
        padding: 0 6px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    /* Confirmation Modal Specific Styles */
    .confirm-modal-mask {
        position: fixed;
        z-index: 1060;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: opacity 0.3s ease;
        padding: 20px;
    }

    .confirm-modal-wrapper {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .confirm-modal-container {
        width: 401px;
        max-width: 401px;
        height: 330px;
        background-color: #FFFFFF;
        border-radius: 8px;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
        padding: 16px 20px 20px 20px;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        overflow: hidden;
        transform: rotate(0deg);
        opacity: 1;
    }

    .confirm-header {
        width: 100%;
    }

    .confirm-title {
        font-size: 14px;
        font-weight: 600;
        color: #111827;
    }

    .btn-close-confirm {
        padding: 0;
        border: none;
        background: transparent;
        color: #111827;
        font-size: 16px;
        cursor: pointer;
        line-height: 1;
    }

    .btn-close-confirm:hover {
        color: #6B7280;
    }

    .confirm-body {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        margin: auto 0;
    }

    .confirm-icon-wrapper {
        margin-bottom: 14px;
    }

    .confirm-icon-wrapper svg {
        display: block;
    }

    .confirm-text {
        font-size: 13px;
        color: #1F2937;
        line-height: 1.4;
    }

    .confirm-text p {
        font-size: 13px;
        color: #1F2937;
    }

    .confirm-text strong {
        font-weight: 700;
        color: #111827;
    }

    .text-muted-sub {
        font-size: 13px;
        color: #374151;
        margin-top: 4px;
    }

    .confirm-actions {
        margin-top: 8px;
    }

    .btn-confirm-batal {
        background: transparent;
        border: none;
        color: #F36F21;
        font-size: 14px;
        font-weight: 500;
        padding: 8px 24px;
        margin-right: 16px;
        cursor: pointer;
        box-shadow: none;
    }

    .btn-confirm-batal:hover {
        color: #D95D16;
        background: transparent;
    }

    .btn-confirm-submit {
        background-color: #F36F21;
        border: 1px solid #F36F21;
        color: #FFFFFF;
        font-size: 14px;
        font-weight: 500;
        border-radius: 6px;
        padding: 9px 36px;
        min-width: 140px;
        box-shadow: none;
        cursor: pointer;
        transition: background-color 0.2s;
    }

    .btn-confirm-submit:hover {
        background-color: #D95D16;
        border-color: #D95D16;
        color: #FFFFFF;
    }

    .modal-enter {
        opacity: 0;
    }

    .modal-leave-active {
        opacity: 0;
    }

    .modal-enter .confirm-modal-container,
    .modal-leave-active .confirm-modal-container {
        -webkit-transform: scale(1.05);
        transform: scale(1.05);
    }
</style>